import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ToolbarComponent } from '../../core/layout/components/toolbar/toolbar.component';
import { FooterComponent } from '../../core/layout/components/footer/footer.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import {
  AzTableComponent,
  AzTableColumn,
} from '../../shared/components/az-table/az-table.component';
import { StatusChangeModalComponent } from '../../shared/components/status-change-modal/status-change-modal.component';
import { DocumentRepositoryService } from './services/document-repository.service';
import { UserPermissionsService } from '../../core/auth/services/user-permissions.service';
import { LoggerService } from '../../core/logging/logger.service';

export interface IgaCoordinatorDisplayRow {
  contactName: string;
  email: string;
  status: string;
  lastSigned: string;
  lastNotified: string;
}

export interface LegalEntityContactDisplayRow {
  contactName: string;
  email: string;
  legalEntityName: string;
  status: string;
  lastSigned: string;
  lastNotified: string;
  _spans?: Record<string, number>;
  _hidden?: Record<string, boolean>;
}

const SLUG_TO_DOCUMENT_TYPE: Record<string, string> = {
  'binding-corporate-rules-eu': 'Binding Corporate Rules EU',
  'data-privacy-standard-eu': 'Data Privacy Standard EU',
  'binding-corporate-rules-uk': 'Binding Corporate Rules UK',
  'data-privacy-standard-uk': 'Data Privacy Standard UK',
};

@Component({
  selector: 'app-document-repository-contacts',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ToolbarComponent,
    FooterComponent,
    SpinnerComponent,
    AzTableComponent,
    StatusChangeModalComponent,
  ],
  providers: [DatePipe],
  templateUrl: './document-repository-contacts.component.html',
  styleUrl: './document-repository-contacts.component.scss',
})
export class DocumentRepositoryContactsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private documentRepositoryService = inject(DocumentRepositoryService);
  private userPermissionsService = inject(UserPermissionsService);
  private logger = inject(LoggerService);
  private datePipe = inject(DatePipe);

  loading = signal(false);
  isErrorModalOpen = signal(false);
  errorMessage = signal('');
  documentTitle = signal('');
  toolbarTitle = computed(() => this.documentTitle().replace(/ EU$| UK$/i, ''));
  igaRows = signal<IgaCoordinatorDisplayRow[]>([]);
  legalEntityRows = signal<LegalEntityContactDisplayRow[]>([]);

  igaColumns: AzTableColumn[] = [
    { key: 'contactName', label: 'Contact Name' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'status', label: 'Status', type: 'tag', width: '145px' },
    { key: 'lastSigned', label: 'Last Signed', width: '118px' },
    { key: 'lastNotified', label: 'Last Notified', width: '118px' },
  ];

  legalEntityColumns: AzTableColumn[] = [
    { key: 'contactName', label: 'Contact Name' },
    { key: 'email', label: 'Email', type: 'email' },
    { key: 'legalEntityName', label: 'Legal Entity Name' },
    { key: 'status', label: 'Status', type: 'tag', width: '145px' },
    { key: 'lastSigned', label: 'Last Signed', width: '118px' },
    { key: 'lastNotified', label: 'Last Notified', width: '118px' },
  ];

  ngOnInit(): void {
    this.initPage();
  }

  private initPage(): void {
    if (!this.userPermissionsService.hasPstMemberRole()) {
      this.router.navigate(['/access-denied']);
      return;
    }

    const slug = this.route.snapshot.paramMap.get('documentSlug') ?? '';
    const documentType = SLUG_TO_DOCUMENT_TYPE[slug];

    if (!documentType) {
      this.router.navigate(['/document-repository']);
      return;
    }

    this.documentTitle.set(documentType);
    this.logger.info(`Document Repository Contacts page loaded for: ${documentType}`);
    this.loadContacts(documentType);
  }

  private formatDate(value: string | null): string {
    if (!value) return '--';
    return this.datePipe.transform(value, 'dd/MMM/yyyy', 'UTC') ?? '--';
  }

  private loadContacts(documentType: string): void {
    this.loading.set(true);
    this.documentRepositoryService.getContacts(documentType).subscribe({
      next: (response) => {
        this.igaRows.set(
          response.contacts.iga_coordinators.map((c) => ({
            contactName: c.contact_name ?? '',
            email: c.email ?? '',
            status: c.status ?? '',
            lastSigned: this.formatDate(c.last_signed),
            lastNotified: this.formatDate(c.last_notified),
          })),
        );
        this.legalEntityRows.set(
          this.applyContactGrouping(
            response.contacts.legal_entity_contacts.map((c) => ({
              contactName: c.contact_name ?? '',
              email: c.email ?? '',
              legalEntityName: c.legal_entity_name ?? '',
              status: c.status ?? '',
              lastSigned: this.formatDate(c.last_signed),
              lastNotified: this.formatDate(c.last_notified),
            })),
          ),
        );
        this.loading.set(false);
      },
      error: (err) => {
        this.logger.error('Failed to load document repository contacts', err);
        const message: string =
          err?.error?.message ?? 'An error occurred while loading contacts. Please try again.';
        this.errorMessage.set(message);
        this.loading.set(false);
        this.isErrorModalOpen.set(true);
      },
    });
  }

  private applyContactGrouping(
    rows: LegalEntityContactDisplayRow[],
  ): LegalEntityContactDisplayRow[] {
    const sorted = [...rows].sort((a, b) =>
      a.email.toLowerCase().localeCompare(b.email.toLowerCase()),
    );
    const result: LegalEntityContactDisplayRow[] = [];
    let i = 0;
    while (i < sorted.length) {
      const currentEmail = sorted[i].email.toLowerCase();
      // Do not group rows with empty email — they are distinct unknown contacts
      if (!currentEmail) {
        result.push(sorted[i]);
        i++;
        continue;
      }
      let groupSize = 1;
      while (
        i + groupSize < sorted.length &&
        sorted[i + groupSize].email.toLowerCase() === currentEmail
      ) {
        groupSize++;
      }
      if (groupSize > 1) {
        result.push({
          ...sorted[i],
          _spans: {
            contactName: groupSize,
            email: groupSize,
          },
        });
        for (let j = 1; j < groupSize; j++) {
          result.push({
            ...sorted[i + j],
            _hidden: {
              contactName: true,
              email: true,
            },
          });
        }
      } else {
        result.push(sorted[i]);
      }
      i += groupSize;
    }
    return result;
  }

  onErrorModalClose(): void {
    this.isErrorModalOpen.set(false);
    this.router.navigate(['/document-repository']);
  }
}
